import {
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import type { FormikProps } from 'formik';

import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { FamilyMemberResidentRelationship } from '@pflegenavi/shared/api';

interface ResidentRelationshipOptionsProps<
  V extends {
    relationship: FamilyMemberResidentRelationship | undefined;
  }
> {
  formik: FormikProps<V>;
  disabled?: boolean;
}

export const ResidentRelationshipOptions = <
  V extends {
    relationship: FamilyMemberResidentRelationship | undefined;
  }
>({
  formik,
  disabled = false,
}: ResidentRelationshipOptionsProps<V>): JSX.Element => {
  const { t } = useTranslation();
  const value = formik.values.relationship;
  const error = formik.touched.relationship
    ? formik.errors.relationship
    : undefined;
  const { setFieldValue } = formik;

  const onChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      setFieldValue('relationship', value as FamilyMemberResidentRelationship);
    },
    [setFieldValue]
  );

  return (
    <Stack direction="column" width="100%">
      <Typography>{t('mangopay.signup.form.relationship.label')}:</Typography>
      <RadioGroup
        data-cy="FamilyMemberResidentRelationship-options"
        row
        value={value}
        onChange={onChange}
      >
        <FormControlLabel
          data-cy="resident-option"
          key={FamilyMemberResidentRelationship.Resident}
          value={FamilyMemberResidentRelationship.Resident}
          control={<Radio />}
          label={t(
            `mangopay.signup.form.relationship.options.${FamilyMemberResidentRelationship.Resident.toLowerCase()}`
          )}
          checked={value === FamilyMemberResidentRelationship.Resident}
          name="resident"
          disabled={disabled}
        />
        <FormControlLabel
          data-cy="family-member-option"
          key={FamilyMemberResidentRelationship.FamilyMember}
          value={FamilyMemberResidentRelationship.FamilyMember}
          control={<Radio />}
          label={t(
            `mangopay.signup.form.relationship.options.${FamilyMemberResidentRelationship.FamilyMember.toLowerCase()}`
          )}
          checked={value === FamilyMemberResidentRelationship.FamilyMember}
          name="family-member"
          disabled={disabled}
        />
        <FormControlLabel
          data-cy="legal-guardian-option"
          key={FamilyMemberResidentRelationship.LegalGuardian}
          value={FamilyMemberResidentRelationship.LegalGuardian}
          control={<Radio />}
          label={t(
            `mangopay.signup.form.relationship.options.${FamilyMemberResidentRelationship.LegalGuardian.toLowerCase()}`
          )}
          checked={value === FamilyMemberResidentRelationship.LegalGuardian}
          name="legal-guardian"
          disabled={disabled}
        />
        <FormControlLabel
          data-cy="other-option"
          key={FamilyMemberResidentRelationship.Other}
          value={FamilyMemberResidentRelationship.Other}
          control={<Radio />}
          label={t(
            `mangopay.signup.form.relationship.options.${FamilyMemberResidentRelationship.Other.toLowerCase()}`
          )}
          checked={value === FamilyMemberResidentRelationship.Other}
          name="other"
          disabled={disabled}
        />
      </RadioGroup>

      {Boolean(error) && typeof error === 'string' && (
        <FormHelperText
          error
          sx={{
            mt: -0.3,
            ml: 0.3,
          }}
        >
          {error}
        </FormHelperText>
      )}
    </Stack>
  );
};
